// Shared Keycloak JWT role decoding.
//
// Roles live in two places in a Keycloak access token: realm roles under
// `realm_access.roles`, and per-client roles under `resource_access[client].roles`.
// A billing:admin composite may surface in either, so both are checked.
//
// This is the single source of truth for that decode. Older call sites
// (hooks/useFieldRole, components/AppShell, components/ReportSchedulePanel) each
// hand-rolled their own identical copy; new code should import from here, and
// those may be migrated to it over time so a change to token handling (e.g.
// base64url, claim-shape changes) lands in one place instead of four.

interface RolePayload {
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
}

/** All roles from realm_access + every resource_access client, as a Set. */
export function rolesFromToken(token?: string | null): Set<string> {
  const roles = new Set<string>();
  if (!token) return roles;
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as RolePayload;
    for (const r of payload.realm_access?.roles || []) roles.add(r);
    for (const client of Object.values(payload.resource_access || {})) {
      for (const r of client.roles || []) roles.add(r);
    }
  } catch {
    // Malformed token → no roles.
  }
  return roles;
}

/** True if the token carries the given role (realm or any client). */
export function hasRole(token: string | undefined | null, role: string): boolean {
  return rolesFromToken(token).has(role);
}

/** Convenience: the billing:admin gate used across the SPA. */
export function hasBillingAdmin(token?: string | null): boolean {
  return hasRole(token, 'billing:admin');
}

/** The audit-log gate (Beau and Jean). UI only: billing-api also requires the
 *  token to be issued by the master realm. */
export function hasAuditRole(token?: string | null): boolean {
  return hasRole(token, 'billing:audit');
}
